import { Button, ButtonGroup, HStack, Stack, Text, useToast } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  getPrettyFreightServiceAddOn,
  isCargoWiseFulfillmentPartner,
  parseCargoWiseFpAccountIds,
} from '@pelicargo/utils';
import { toCents, toDollars } from '@pelicargo/utils';
import { FreightServiceAddOn, LineItemType, ShipmentFormPayment } from '@prisma/client';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { trpc, TrpcInputs, TrpcOutputs } from '../../../config/trpc';
import { useShipmentFormMutations } from '../../../hooks/shipmentForm/useShipmentFormMutations';
import { ShipmentForm } from '../../../hooks/shipmentForm/useShipmentForms';
import { usePeliConfig } from '../../../hooks/usePeliConfig';
import { ContactSelectInput } from '../../ContactSelectInput';
import { CopyableText } from '../../CopyableText';
import { BookingSection } from '../../Forms/AfrBookingForm';
import { SimpleForm } from '../../SimpleForm';
import { SimpleLabeledPopoverInput } from '../../SimpleLabeledPopoverInput';
import { CargoSelectionList } from './CargoSelectionList';

type Cargo = TrpcOutputs['getCargosByRequestId'][number];
type ToggleLineItemInput = TrpcInputs['toggleLineItem'];

type Props = {
  form: ShipmentForm;
  cargoList?: Cargo[];
  isMawb?: boolean;
  hasHawbs?: boolean;
  isAesFiledFromInlandQuote?: boolean;
  inlandQuoteVariantIdForAes?: number;
};

const paymentOptions = Object.values(ShipmentFormPayment).map((value) => ({
  label: value === ShipmentFormPayment.PRE_PAID ? 'Pre-paid' : 'Collect',
  value,
}));

type PullResult = TrpcOutputs['pullShipmentForm'];
type PushResult = TrpcOutputs['pushShipmentForm'];

/**
 * CargoWise job keys are prefixed by record kind — `C…` for a consol, `S…` for a
 * shipment. Worth labelling: a freshly pushed house is seeded with its parent's
 * consol key and only picks up its own shipment key on the next pull, so without
 * this the MAWB and its HAWBs would show one value with no hint why.
 */
const integrationRecordLabel = (key?: string | null) => {
  if (key?.trim().toUpperCase().startsWith('C')) return 'Consol';
  if (key?.trim().toUpperCase().startsWith('S')) return 'Shipment';
  return 'ID';
};

export const AwbInstructionForm = ({
  form,
  cargoList,
  isMawb,
  hasHawbs,
  isAesFiledFromInlandQuote,
  inlandQuoteVariantIdForAes,
}: Props) => {
  const toast = useToast();
  const { updateShipmentForm, pullShipmentForm, pushShipmentForm, syncShipmentForm, isPulling, isPushing, isSyncing } =
    useShipmentFormMutations();
  const toggleLineItem = trpc.toggleLineItem.useMutation();
  const aesLabel = getPrettyFreightServiceAddOn(FreightServiceAddOn.AES);

  const cargoWiseBusy = isPulling || isPushing || isSyncing;

  /**
   * CargoWise is live for the fulfillment partners listed in Peli config. For every other
   * shipment a push or pull only ever comes back skipped, so the whole block — job key
   * included — is hidden rather than shown as controls that quietly do nothing.
   *
   * The config load is a query, so the block stays hidden for the first render and appears
   * once it resolves. That way round is deliberate: briefly missing controls beats offering
   * a push against a partner the API would refuse.
   */
  const cargoWiseConfig = usePeliConfig('cargowise');
  const showCargoWise = useMemo(
    () =>
      isCargoWiseFulfillmentPartner(
        form.quote,
        parseCargoWiseFpAccountIds(cargoWiseConfig.fulfillmentPartnerAccountIds),
      ),
    [form.quote, cargoWiseConfig.fulfillmentPartnerAccountIds],
  );

  // Narrow with `in` rather than the `skipped` flag: the tRPC-inferred union
  // widens the literal discriminant to `boolean`, so structural checks are safer.
  const notifyPull = (result: PullResult) => {
    if ('reason' in result) {
      toast({ title: 'CargoWise pull skipped', description: result.reason, status: 'warning' });
    } else if ('summary' in result) {
      const parts = [
        result.updatedFields.length ? `Updated ${result.updatedFields.length} field(s)` : 'No fields changed',
        result.shipmentStatus ? `status → ${result.shipmentStatus}` : undefined,
      ].filter(Boolean);
      toast({ title: 'Pulled from CargoWise', description: parts.join(' · '), status: 'success' });
    } else {
      toast({ title: 'Not found in CargoWise', description: result.failureReason, status: 'warning' });
    }
  };

  const notifyPush = (result: PushResult) => {
    if ('reason' in result) {
      toast({ title: 'CargoWise push skipped', description: result.reason, status: 'warning' });
    } else {
      toast({ title: 'Pushed to CargoWise', status: 'success' });
    }
  };

  const runCargoWise = async (action: 'pull' | 'push' | 'sync') => {
    try {
      if (action === 'pull') {
        notifyPull(await pullShipmentForm({ id: form.id }));
      } else if (action === 'push') {
        notifyPush(await pushShipmentForm({ id: form.id }));
      } else {
        const result = await syncShipmentForm({ id: form.id });
        notifyPull(result.pull);
        notifyPush(result.push);
      }
    } catch (error: any) {
      toast({ title: `CargoWise ${action} failed`, description: error?.message, status: 'error' });
    }
  };

  const defaultValues = useMemo<Record<string, any>>(
    () => ({
      is_aes_filed: isMawb ? !!isAesFiledFromInlandQuote : false,
      itn_number: form.itn_number ?? '',
      shipper_contact_id: form.shipper_contact_id ?? null,
      consignee_contact_id: form.consignee_contact_id ?? null,
      notify_contact_id: form.notify_contact_id ?? null,
      handling_instructions: form.handling_instructions ?? '',
      accounting_info: form.accounting_info ?? '',
      payment: form.payment,
      target_rate: toDollars(form.target_rate),
      nature_of_goods: form.nature_of_goods ?? '',
      hs_code: form.hs_code ?? '',
    }),
    [form, isAesFiledFromInlandQuote, isMawb],
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues });

  useEffect(() => {
    methods.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const handleValid = async (fields: Record<string, any>) => {
    const isAesToggleUpdate = Object.prototype.hasOwnProperty.call(fields, 'is_aes_filed');

    if (isAesToggleUpdate && isMawb && inlandQuoteVariantIdForAes) {
      const shouldHaveAesFiling = !!fields.is_aes_filed;
      const togglePayload: ToggleLineItemInput = {
        quote_variant_id: inlandQuoteVariantIdForAes,
        label: aesLabel,
        enabled: shouldHaveAesFiling,
        type: LineItemType.OTHER,
        cost: 2500, // $25.00 in cents
      };
      await toggleLineItem.mutateAsync(togglePayload);
      return; // AES toggle is handled by the dedicated toggle procedure.
    }

    const next: Record<string, any> = fields;
    if ('target_rate' in next) next.target_rate = toCents(next.target_rate);
    await updateShipmentForm({ id: form.id, ...next });
  };

  const selectedCargoIds = useMemo(() => form.cargo?.map((cargo) => cargo.id) ?? [], [form.cargo]);

  return (
    <SimpleForm onValid={handleValid} defaultValues={defaultValues} {...methods}>
      <Show if={showCargoWise}>
        <HStack justify="space-between" mb={4}>
          <HStack spacing={2} align="center">
            <Text fontSize="sm" fontWeight="medium" color="gray.500">
              CargoWise
            </Text>
            {/* The matched job key, only once a push/pull has echoed one back. Labelled by
                record kind because a house shows its parent's consol key until its first
                pull resolves its own shipment key — see integrationRecordLabel. */}
            <Show if={!!form.integration_id}>
              <HStack spacing={1} align="center">
                <Text fontSize="xs" color="gray.400">
                  {integrationRecordLabel(form.integration_id)}
                </Text>
                <CopyableText value={form.integration_id!} fontSize="xs" fontFamily="mono" color="gray.600" />
              </HStack>
            </Show>
          </HStack>
          {/* type="button" so these never submit the surrounding form. */}
          <ButtonGroup size="xs" variant="outline" isDisabled={cargoWiseBusy}>
            <Button type="button" onClick={() => runCargoWise('pull')} isLoading={isPulling}>
              Pull
            </Button>
            <Button type="button" onClick={() => runCargoWise('push')} isLoading={isPushing}>
              Push
            </Button>
            <Button
              type="button"
              colorScheme="primary"
              variant="solid"
              onClick={() => runCargoWise('sync')}
              isLoading={isSyncing}
            >
              Sync
            </Button>
          </ButtonGroup>
        </HStack>
      </Show>
      <Stack direction={{ base: 'column', lg: 'row' }} spacing={6} align="flex-start">
        <Stack flex={1} spacing={6}>
          {isMawb && (
            <BookingSection title="Filing">
              <SimpleLabeledPopoverInput
                isHorizontal
                name="is_aes_filed"
                label={aesLabel}
                inputType="checkbox"
                isDisabled={!inlandQuoteVariantIdForAes}
              />
              <SimpleLabeledPopoverInput isHorizontal name="itn_number" label="ITN Number" />
            </BookingSection>
          )}
          <BookingSection title="Parties">
            <ContactSelectInput name="shipper_contact_id" label="Shipper" />
            <ContactSelectInput name="consignee_contact_id" label="Consignee" />
            <ContactSelectInput name="notify_contact_id" label="Notify Party" />
          </BookingSection>
          <BookingSection title="Handling">
            <SimpleLabeledPopoverInput
              isHorizontal
              name="handling_instructions"
              label="Handling Instructions"
              inputType="textarea"
            />
            <Show if={!isMawb || !hasHawbs}>
              <SimpleLabeledPopoverInput
                isHorizontal
                name="nature_of_goods"
                label="Nature of goods"
                inputType="textarea"
              />
              <SimpleLabeledPopoverInput isHorizontal name="hs_code" label="HS Code(s)" />
            </Show>
          </BookingSection>
        </Stack>
        <Stack flex={1} spacing={6}>
          <BookingSection title="Accounting">
            <SimpleLabeledPopoverInput
              isHorizontal
              name="payment"
              label="Payment"
              inputType="select"
              options={paymentOptions}
            />
            <SimpleLabeledPopoverInput
              isHorizontal
              name="target_rate"
              label="Rate"
              inputType="number"
              formatDisplayValue={(value) => (value == null || value === '' ? '-' : `$${value}`)}
            />
            <SimpleLabeledPopoverInput
              isHorizontal
              name="accounting_info"
              label="Accounting Info"
              inputType="textarea"
            />
          </BookingSection>
          {/* Cargo belongs to house bills (HAWBs), not the MAWB. Match the
              customer flow, which only allows cargo selection on HAWBs. */}
          {!isMawb && (
            <CargoSelectionList
              cargoList={cargoList}
              value={selectedCargoIds}
              formId={form.id}
              onChange={(ids) => updateShipmentForm({ id: form.id, cargo: ids })}
            />
          )}
        </Stack>
      </Stack>
    </SimpleForm>
  );
};
